import { cspInfractions } from './infractions/csp';
import { dkimInfractions } from './infractions/dkim';
import { dmarcInfractions } from './infractions/dmarc';
import { spfInfractions } from './infractions/spf';
import { hstsInfractions } from './infractions/hsts';
import { httpInfractions } from './infractions/http';
import { tlsInfractions } from './infractions/tls';
import { dnssecInfractions } from './infractions/dnssec';
import infoFindingsI18n from './info-findings/i18n';
import { portInfractions as remoteAccessPortInfractions } from './infractions/remoteAccessPorts';
import { portInfractions as webServicePortInfractions } from './infractions/webServicePorts';
import { portInfractions as infrastructurePortInfractions } from './infractions/infrastructurePorts';
import { portInfractions as emailPortInfractions } from './infractions/emailPorts';
import { portInfractions as fileSharingPortInfractions } from './infractions/fileSharingPorts';
import { portInfractions as databasePortInfractions } from './infractions/databasePorts';
import { portServiceVulnerableI18n } from './infractions/portServiceVulnerableI18n';

export default {
  en: {
    menu: {
      scans: 'Scans',
      assets: 'Assets',
      findings: 'Findings',
    },
    categories: {
      dnsSecurity: 'DNS security',
      emailSecurity: 'Email security',
      webSecurity: 'Web security',
      portSecurity: 'Port security',
    },
    categoryDescription: {
      dnsSecurity:
        'These controls ensure your DNS infrastructure is operating efficiently and reliably.',
      emailSecurity: 'These controls verify email legitimacy and build sending reputation.',
      webSecurity:
        'These configurations ensure secure content delivery and prevent malicious code.',
      portSecurity: `Port security identifies vulnerabilities by analysing your system's network ports, focussing on commonly exploited ports.`,
    },
    statusIcon: {
      altText: {
        good: 'Low',
        okay: 'Medium',
        bad: 'High/Critical',
      },
    },
    supplierMonitoring: {
      title: 'External monitoring',
      beta: 'Beta version: this is an early release and features may change based on feedback.',
      assetStatus: {
        ACTIVE: 'Active',
        DISABLED: 'Inactive',
        label: 'Status',
      },
      scanSeverity: {
        CRITICAL: 'Critical',
        HIGH: 'High',
        MEDIUM: 'Medium',
        LOW: 'Low',
        INFO: 'Information',
        label: 'Severity',
      },
      activeAssets: 'Active assets',
      activeAssetsDescription:
        'Regular security monitoring is running for these assets. Security checks include email authentication and web security configuration.',
      disabledAssets: 'Inactive assets',
      disabledAssetsDescription:
        'Security monitoring is paused or unavailable for these assets. This could be due to manual pause or connection issues.',
      createdAt: 'Added',
      createdBy: 'Added by',
      lastModifiedAt: 'Updated at',
      lastModifiedBy: 'Updated by',
      editAssetButton: 'Edit asset',
      addAssetButton: 'Add asset',
      addAssetModalTitle: 'Add asset',
      editAssetModalTitle: 'Edit asset',
      assetValue: 'Asset',
      assetValuePlaceholder: 'example.com',
      invalidDomainOrSubdomain: 'Please enter a valid domain or subdomain',
      assetDescription: 'Description of asset',
      assetDescriptionPlaceholder: 'Production website',
      assetDescriptionHelpText: 'Add any notes that help identify this asset to your clients',
      saveAssetButton: 'Save asset',
      deleteAsset: 'Delete asset',
      deleteAssetSuccess: 'Asset deleted successfully',
      deleteAssetError: 'Error deleting asset',
      editAssetError: 'Error saving asset',
      addAssetError: 'Error adding asset',
      addAssetSuccess: 'Asset added successfully',
      saveAssetSuccess: 'Asset saved successfully',
      rescanAssetError: 'Error triggering asset re-scan',
      assetCreatedBySystem: 'System',
      cancelButton: 'Cancel',
      emptyTitle: "You haven't added any assets yet",
      emptySubtitle: 'Add your first asset so we can display ',
      emptyCTA: 'Add an asset',
      systemStatusUpdateNotAllowed: "Your organisation's website cannot be set to inactive",
      registeredWebsiteSupplier: 'Registered website',
      registeredWebsite: 'Registered website',
      registeredWebsiteInOrganisationSettings:
        'Your registered website can be updated in your {settings}',
      organisationSettings: 'organisation settings',
      allowances: {
        title: 'Assets',
        count: '{n} / {m}',
        tooltip:
          'You may include up to {allowed} assets as part of your profile. To request to add more assets, contact our support team.',
        request: 'Request capacity',
      },
    },
    supplierMonitoringScans: {
      emptyTitle: "You don't have any scans yet",
      emptySubtitle: 'You will need to add an asset before we can display any scans',
      emptyCTA: 'Add an asset',
      scanningTitle: 'We are scanning your asset',
      scanningSubtitle: 'Come back in a few minutes to see the results',
      feedbackTitle: 'Give us your feedback',
      feedbackText:
        "We are still building out this feature and would love to hear how you're finding it.",
      infractionType: 'issue',
      infoFindingType: 'info',
      feedbackCTA: 'Send us feedback',
      exportFindingsTitle: 'Latest-Findings',
      exportTitle: 'Scan-Results',
      exportCSVButton: 'Export as CSV',
      exportHeaderAsset: 'Asset',
      exportHeaderScannedAt: 'Scanned at',
      exportHeaderScanType: 'Scan type',
      exportHeaderContextNote: 'Context note',
      exportHeaderPrivateNote: 'Private note',
      exportHeaderScanOutput: 'Scan output',
      exportHeaderFindingType: 'Finding type',
      exportHeaderFindingName: 'Finding name',
      exportHeaderFindingExplanation: 'Finding explanation',
      exportHeaderFindingSeverity: 'Finding severity',
      exportHeaderFindingSeverityLevel: 'Finding severity level',
      exportHeaderFindingContext: 'Finding context',
    },
    unclaimedSupplierMonitoringScans: {
      emptyTitle: 'This supplier has no assets to be monitored',
      emptySubtitle:
        'Results will be available when the supplier claims this profile and adds assets',
      scanningTitle: "This supplier's assets are currently being scanned",
      scanningSubtitle: 'The results will be available in a few minutes',
    },
    supplierTab: {
      emptyTitle: 'This supplier has not set up any assets to be monitored',
      emptySubtitle: 'Contact the supplier if you want them to share an asset',
      emptyCTA: 'Start a discussion',
      assetsTitle: 'Assets',
      assetsSubtitle:
        'These are the assets we are scanning to understand the security position of this supplier from an external monitoring perspective',
      moreDomainsTitle: 'Not seeing an asset you use?',
      moreDomainsSubtitle:
        'Reach out to this supplier if you believe they should add an asset your organisation uses',
      moreDomainsCTA: 'Start a discussion',
    },
    associatedAsset: {
      unknown: 'Unknown asset',
      deleted: 'This asset has been deleted',
    },
    scanResult: {
      scanDescriptionTitle: 'What does this scan type check for?',
      scanLearnMore: 'Learn more about this scan',
      domains: 'Assets scanned',
      associatedControls: 'This scan relates to control: | This scan relates to controls:',
      associatedScans: 'This control relates to scan: | This control relates to scans:',
      scanType: 'Scan type',
      unableScanPorts: 'Unable to scan ports',
      riskIndicatorTooltip: {
        findings: '{count} {severity} issue | {count} {severity} issues',
        assets: '{assets} asset | {assets} assets',
        tooltip: '{findings} identified on {assets}',
      },
      scanRiskIndicatorTooltip: '{count} {severity} issue found | {count} {severity} issues found',
      scanTypes: {
        SPF: {
          title: 'Are SPF records protecting the email domain?',
          description:
            'SPF (Sender Policy Framework) helps prevent email spoofing by specifying which servers can send email from your domain. Your SPF records need to list all legitimate email sending services while blocking unauthorised senders.',
          infractions: spfInfractions,
          displayName: 'SPF',
        },
        DKIM: {
          title: "Is DKIM protecting the domain's emails from tampering?",
          description:
            'We monitor your DKIM (DomainKeys Identified Mail) configuration to verify your emails are digitally signed and protected against tampering.',
          infractions: dkimInfractions,
          displayName: 'DKIM',
        },
        DMARC: {
          title: "Is DMARC enforcing the domain's email security policies?",
          description:
            'We monitor your DMARC (Domain-based Message Authentication, Reporting, and Conformance) configuration to ensure you have a strong policy that tells receiving servers how to handle suspicious emails from your domain.',
          infractions: dmarcInfractions,
          displayName: 'DMARC',
        },
        HSTS: {
          title: 'Is HSTS enforcing secure connections to your domain?',
          description:
            'We monitor your HSTS (HTTP Strict Transport Security) configuration to ensure browsers only connect to your domain using secure HTTPS connections.',
          infractions: hstsInfractions,
          displayName: 'HSTS',
        },
        CSP: {
          title: 'Is the Content Security Policy (CSP) protecting the domain from malicious code?',
          description:
            'We monitor your CSP (Content Security Policy) configuration to ensure your website only loads resources from trusted sources and prevents code injection attacks.',
          infractions: cspInfractions,
          displayName: 'CSP',
        },
        TLS: {
          title: 'Is TLS configured securely on the domain?',
          description:
            "Transport Layer Security (TLS) creates an encrypted connection between your website and its visitors. We monitor your TLS configuration to ensure it's properly securing data in transit.",
          infractions: tlsInfractions,
          displayName: 'TLS',
        },
        SecurityHeaders: {
          title: 'Are HTTP security headers protecting the domain?',
          description:
            "HTTP security headers tell browsers how to handle your website's content securely. We monitor these headers to ensure they're properly configured to prevent common web attacks.",
          infractions: httpInfractions,
          displayName: 'Security Headers',
        },
        DNS: {
          title: 'Is the DNS configuration secure?',
          description:
            'We monitor your DNS (Domain Name System) configuration to ensure your domain is correctly resolving to the right IP address and is protected against DNS spoofing attacks.',
          infractions: dnssecInfractions,
          displayName: 'DNS',
        },
        RemoteAccessPorts: {
          title: 'Remote access ports',
          description:
            'Remote access ports are checked to identify potentially insecure services that could be used to gain entry to your network. We check for services such as SSH (port 22), RDP (port 3389), and VNC (port 5900).',
          infractions: {
            ...remoteAccessPortInfractions,
            ...portServiceVulnerableI18n,
          },
          displayName: 'Remote Access Ports',
        },
        DatabasePorts: {
          title: 'Database ports',
          description:
            'Database ports are checked to ensure they are not unnecessarily exposed to the internet, which could risk a data breach. We check for services such as Microsoft SQL Server (port 1433), PostgreSQL (port 5432), and Redis (port 6379).',
          infractions: {
            ...databasePortInfractions,
            ...portServiceVulnerableI18n,
          },
          displayName: 'Database Ports',
        },
        WebServicePorts: {
          title: 'Web service ports',
          description:
            'Web service ports are checked to confirm which services are running and to identify any using unencrypted connections. We check for services such as HTTP (port 80) and HTTPS (port 443).',
          infractions: {
            ...webServicePortInfractions,
            ...portServiceVulnerableI18n,
          },
          displayName: 'Web Service Ports',
        },
        FileSharingPorts: {
          title: 'File sharing ports',
          description:
            'File sharing ports are checked to prevent unauthorised access or the insecure transfer of data from your network. We check for services such as SMB (port 445), NFS (port 2049), and AFP (port 548).',
          infractions: {
            ...fileSharingPortInfractions,
            ...portServiceVulnerableI18n,
          },
          displayName: 'File Sharing Ports',
        },
        EmailPorts: {
          title: 'Email ports',
          description:
            'Email ports are checked to detect unencrypted services that could expose credentials and email content. We check for services such as SMTP (port 25), POP3 (port 110), and IMAP (port 143).',
          infractions: {
            ...emailPortInfractions,
            ...portServiceVulnerableI18n,
          },
          displayName: 'Email Ports',
        },
        InfrastructurePorts: {
          title: 'Infrastructure ports',
          description:
            'Infrastructure ports are checked for essential network services that, if misconfigured, could be exploited to disrupt or redirect your traffic. We check for services such as DNS (port 53), Kerberos (port 88), and LDAP (port 389).',
          infractions: {
            ...infrastructurePortInfractions,
            ...portServiceVulnerableI18n,
          },
          displayName: 'Infrastructure Ports',
        },
      },
      error: 'Scan unsuccessful',
      unknownAsset: 'Unknown asset',
      noIssues: 'No issues found',
      tabs: {
        overview: 'Overview',
        notes: 'Private notes',
        discussions: 'Discussions',
      },
      scanScheduled: 'Asset scan scheduled',
      scan: 'Scan: ',
      expandScan: 'Expand scan information',
      findingsCount: '{n} finding | {n} findings',
      openPortCount: '{n} open port | {n} open ports',
      noFindings: 'We scanned this asset and found no issues',
      lastScanned: 'Last scanned',
      scanInProgress: 'Scan in progress:',
      scanInProgressText: 'this may take a few minutes',
      rescan: 'Scan now',
      issues: 'Issues',
      information: 'Information',
      allFindings: 'All Findings',
      criticality: 'Criticality',
      hasRisks: 'Has risks',
      hasDiscussions: 'Has discussions',
      hasNotes: 'Has notes',
      basicConnection: "You don't have access to this asset",
      allScansUnsuccessful: 'All scans were unsuccessful',
      port: 'Port',
      ip: 'IP',
      waf: 'WAF',
      whyRisk: 'Why is this a risk?',
      cve: 'CVE',
      hasKev: 'This CVE is listed in the CISA Known Exploited Vulnerabilities (KEV) catalog.',
      hasHighEpss: 'EPSS score: {epss} — high likelihood of exploitation in the wild.',
      unknownServiceVersion: 'unknown',
    },
    contextNote: {
      emptyTitle: 'Give context to your clients on these findings',
      emptyCTA: 'Add a note',
      emptyCTAAlt: 'Add finding notes',
      CTA: 'Edit finding notes',
      note: 'Note',
      description: "Add context for your clients about this scan's findings",
      context: "Add context for your clients about this scan's findings",
      remove: 'Remove note',
    },
    clientAssetScanNote: {
      placeholder: 'Write a message...',
      visibleLabel: 'Only visible to {0}',
    },
    onboardingModal: {
      title: 'External monitoring',
      subHeading: 'Monitor your public domains for free',
      introduction:
        'Risk Ledger now offers monitoring of your public domains, so you and select clients can have a more holistic view of your security posture.',
      setup: "You'll be able to:",
      bullet1:
        'Get visibility on security issues you might not know about and notifications when issues arise',
      bullet2: 'Understand how your domains look to both clients and attackers',
      conclusion: 'This service is {free} and you are in control of the domains that are scanned.',
      free: 'free',
      closeButton: 'See my scans',
    },
    portModal: {
      comingSoon: 'Coming soon to External Monitoring',
      heading: 'Port Scanning',
      introduction:
        'Our upcoming port scanning feature gives you visibility into your external attack surface. It scans the most commonly exploited ports including remote access, databases and web services.',
      enable: 'Port scanning will help you:',
      bullet1: 'Identify potentially vulnerable open ports',
      bullet2: 'Prioritise which ports to secure first',
      access: 'Get early access to port scanning in External Monitoring',
      interestedButton: 'Register your interest',
      notInterestedButton: 'Not now',
    },
    addDomainBanner: {
      title: 'Add your assets',
      desc: "We've already scanned your registered website. Add any other assets your clients engage with, or that you want to assess for security.",
      button: 'Add another asset',
    },
    infoFindings: {
      ...infoFindingsI18n.en,
    },
    snippets: {
      copied: 'Copied',
    },
    severityLegend: {
      title: 'Severities',
      button: 'Learn about severity',
      link: 'Learn more about risk levels',
      critical: {
        title: 'Critical',
        description:
          'Absence of fundamental security controls or critical misconfigurations that create immediate, exploitable vulnerabilities. Direct impact on system security and data protection. No mitigating controls in place.',
      },
      high: {
        title: 'High',
        description:
          'Major security control gaps or misconfigurations that significantly weaken system security. Could lead to system compromise with minimal additional conditions. Partial mitigating controls may exist.',
      },
      medium: {
        title: 'Medium',
        description:
          'Security controls present but suboptimal. Vulnerabilities require specific conditions to exploit. Limited impact or requires elevated privileges. Basic security measures exist but need improvement.',
      },
      low: {
        title: 'Low',
        description:
          'Minor security improvements needed. Core security intact but not following all best practices. Minimal real-world impact. Optimization opportunities rather than direct vulnerabilities.',
      },
      informational: {
        title: 'Informational',
        description:
          'A security indicator that provides useful context or highlights a potential error. Unlike an infraction, an informational finding does not represent a breach of best security practice, but it may offer insights or suggest areas for review.',
      },
      unable: {
        title: 'Unable to scan',
        description: 'Due to technical issues, the asset cannot be scanned.',
      },
      pending: {
        title: 'Scan pending',
        description: 'Due to technical issues, the asset scan is in progress.',
      },
    },
    severityExplanation: {
      title: 'Why is this {severity}?',
    },
    findings: {
      title: 'Findings',
      description: 'View all security findings from the latest scan results',
      supplierDescription: 'View security findings from the latest scan results for this supplier',
      entityName: 'finding | findings',
      columns: {
        finding: 'Finding',
        assetValue: 'Asset',
        category: 'Category',
        severityLevel: 'Severity',
        scanType: 'Scan Type',
        scannedAt: 'Scanned At',
      },
      filters: {
        filterResultsFindings: '{count} findings | {count} finding | {count} findings',
        filterResultsOutOf: '{0} out of {1}',
        assetValue: 'Asset',
        severityLevel: 'Severity Level',
        code: 'Code',
        scanType: 'Scan Type',
        noSeverity: 'No Severity',
        scanCategory: 'Scan Category',
      },
      typeLabels: {
        INFRACTION: 'Infraction',
        INFORMATIONAL: 'Informational',
      },
      placeholders: {
        errorTitle: 'Unable to load findings',
        errorDescription: 'There was an error loading the findings data. Please try again.',
        emptyTitle: 'No findings found',
        emptyDescription: 'No security findings match your current search criteria.',
      },
    },
  },
};
